<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';

import { useConfirmStore } from '@/stores/confirm';

const confirm = useConfirmStore();

const request = computed(() => confirm.request);
const okButton = ref<HTMLButtonElement | null>(null);
const typeField = ref<HTMLInputElement | null>(null);

const canConfirm = computed(() => {
  const required = request.value?.typeToConfirm;
  if (!required) return true;
  return confirm.typed.trim().toLowerCase() === required.toLowerCase();
});

// Focus the safe path by default, so Enter never confirms something
// destructive the player has not read.
watch(request, async (value) => {
  if (!value) return;
  await nextTick();
  if (value.typeToConfirm) typeField.value?.focus();
  else okButton.value?.focus();
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="request"
      class="confirm"
      role="dialog"
      aria-modal="true"
      @keyup.esc="confirm.cancel()"
    >
      <div class="confirm__scrim" @click="confirm.cancel()"></div>

      <div class="confirm__panel panel">
        <div class="panel__header">
          <h2>{{ request.title }}</h2>
        </div>
        <div class="panel__body stack">
          <p class="confirm__body">{{ request.body }}</p>

          <div v-if="request.typeToConfirm">
            <label for="confirm-type">
              Type <strong>{{ request.typeToConfirm }}</strong> to continue
            </label>
            <input
              id="confirm-type"
              ref="typeField"
              v-model="confirm.typed"
              autocomplete="off"
              @keyup.enter="canConfirm && confirm.confirm()"
            />
          </div>

          <div class="confirm__actions">
            <button class="btn" @click="confirm.cancel()">
              {{ request.cancelLabel ?? 'Cancel' }}
            </button>
            <button
              ref="okButton"
              class="btn"
              :class="request.danger ? 'btn--danger' : 'btn--primary'"
              :disabled="!canConfirm"
              @click="confirm.confirm()"
            >
              {{ request.confirmLabel ?? 'Confirm' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
.confirm {
  position: fixed;
  inset: 0;
  z-index: 200;
  display: grid;
  place-items: center;
  padding: 1rem;

  &__scrim {
    position: absolute;
    inset: 0;
    background: rgb(8 7 6 / 68%);
  }

  &__panel {
    position: relative;
    width: min(420px, 100%);
    box-shadow: 0 12px 40px rgb(0 0 0 / 55%);
  }

  &__body {
    margin: 0;
    font-size: 0.9rem;
    line-height: 1.5;
    color: var(--parchment-dim);
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
  }
}
</style>
